import React, { useEffect, useState } from 'react';
import { CreateButton, FormGroup, InputField, Label, ModalTitle, Select } from '../SharedComponents';
import { Autocomplete, Box, CircularProgress, TextField, Checkbox, FormControlLabel, Typography } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import styled from 'styled-components';
import { isEqual } from 'lodash';
import { OrderInterface, OrderVariantInterface, ProductInterface, AddonInterface } from '../../types';
import apiClient from '../../services/apiClient';
import CustomDatePicker from '../CustomDatePicker';
import { Moment } from 'moment';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { getCurrency } from '../../services/tableHelper';

interface EditOrderFormProps {
	order: any;
	variants: any;
	isOrderVariantsLoading: boolean;
	cancelEdit: () => void;
	handleHidePanel: (order?: OrderInterface) => void;
	onDeleteClick?: () => void;
}

const variantItemWrapper = {
	display: 'flex',
	flexDirection: 'column',
	justifyContent: 'space-between',
	flexWrap: 'wrap',
	padding: '10px',
	border: '1px solid #c4c4c4',
	borderRadius: '8px',
	marginBottom: '10px',
	boxShadow: '0px 3px 4px 0px rgb(221 232 235 / 61%)',
	'@media (max-width: 768px)': {
		// flexDirection: 'column'
	},
};

const variantSelectStyle = {
	maxWidth: '100%',
};

const variantInputWrapperStyle = {
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'center',
	width: '100%',
};

const variantUnitPriceWrapper = {
	marginRight: '20px',
	maxWidth: '25%',
};

const variantQuantityWrapper = {
	maxWidth: '25%',
};

const fieldRowStyle = {
	display: 'flex',
	flexDirection: 'row',
	justifyContent: 'space-between',
	'@media (max-width: 768px)': {
		flexWrap: 'wrap',
		flexDirection: 'column',
	},
};

const AddVariantButton = styled.div`
	padding: 10px;
	width: 100%;
	border-radius: 8px;
	border: 1px dashed black;
	color: black;
	cursor: pointer;
	margin-top: 20px;
	text-align: center;
	box-sizing: border-box;
`;

const fetchOrderInventory = () => {
	return apiClient.get('/inventory/order-inventory');
};

const fetchCustomers = (searchTerm: string) => {
	return apiClient.get(`/customers?search=${searchTerm}`);
};

const submitOrder = (orderData: OrderInterface) => {
	const isEditing = orderData.id ? true : false;

	if (isEditing) {
		return apiClient.update(`/orders/${orderData.id}`, orderData);
	}

	return apiClient.post('/orders', orderData);
};

interface EditOrderModalProductsListInterface {
	variants: OrderVariantInterface[];
	handleChange: (
		e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
		index: number,
		orderInventory?: any[]
	) => void;
	addVariantSlot: (a: number, b: number) => void;
	removeVariantSlot: (index: number) => void;
	isNewOrder: boolean;
	handleAddonChange: (
		orderIndex: number,
		addonId: number,
		addonGroupId: number,
		quantity: number,
		price: number
	) => void;
}

const EditOrderModalProductsList = ({
	variants: orderVariants,
	handleChange,
	addVariantSlot,
	removeVariantSlot,
	isNewOrder,
	handleAddonChange,
}: EditOrderModalProductsListInterface) => {
	const [orderInventory, setOrderInventory] = useState<any[]>([]);
	const [isFetchingInventory, setIsFetchingInventory] = useState<boolean>(false);
	const currency = useSelector((state: any) => getCurrency(state.auth.user.currency));

	useEffect(() => {
		setIsFetchingInventory(true);

		fetchOrderInventory()
			.then((response) => {
				console.log('order inventory response', response);
				let orderInventoryData = response.data ? response.data : [];
				setOrderInventory(orderInventoryData);
				setIsFetchingInventory(false);
			})
			.catch((error) => {
				console.error('Error fetching order inventory:', error);
				setIsFetchingInventory(false);
			});
	}, []);

	// Prevent scroll on number inputs
	useEffect(() => {
		const handleWheel = (e: WheelEvent) => {
			const target = e.target as HTMLElement;
			if (target.tagName === 'INPUT' && (target as HTMLInputElement).type === 'number') {
				e.preventDefault();
				(target as HTMLInputElement).blur();
			}
		};

		document.addEventListener('wheel', handleWheel, { passive: false });
		return () => {
			document.removeEventListener('wheel', handleWheel);
		};
	}, []);

	if (isFetchingInventory) {
		return (
			<div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', padding: '20px 0px' }}>
				<CircularProgress size={30} />
			</div>
		);
	}

	const getProductById = (productId: number) => {
		return orderInventory.find((product) => product.id === productId);
	};

	const renderAddonSelection = (orderVariant: OrderVariantInterface, orderIndex: number, product: any) => {
		if (!product.addon_groups || product.addon_groups.length === 0) {
			return null;
		}

		return (
			<div style={{ marginTop: '10px', padding: '10px', backgroundColor: '#f9fafb', borderRadius: '4px' }}>
				<Typography variant="subtitle2" style={{ marginBottom: '10px' }}>
					Addons:
				</Typography>
				{product.addon_groups.map((addonGroup: any) => (
					<div key={addonGroup.id} style={{ marginBottom: '15px' }}>
						<Typography variant="body2" style={{ fontWeight: 'bold', marginBottom: '5px' }}>
							{addonGroup.name}
							{addonGroup.is_required && <span style={{ color: 'red' }}>*</span>}
						</Typography>
						<Typography variant="caption" style={{ color: '#666', marginBottom: '5px', display: 'block' }}>
							Select {addonGroup.min_addons_select || 0} - {addonGroup.max_addons_select || 'unlimited'}
						</Typography>
						{addonGroup.addons?.map((addon: any) => {
							const selectedAddon = orderVariant.addons?.find((a: any) => a.id === addon.id);
							const isSelected = !!selectedAddon;
							const quantity = selectedAddon?.quantity || 0;

							return (
								<div
									key={addon.id}
									style={{ display: 'flex', alignItems: 'center', marginBottom: '5px' }}
								>
									<FormControlLabel
										control={
											<Checkbox
												checked={isSelected}
												size="small"
												onChange={(e) => {
													if (e.target.checked) {
														handleAddonChange(
															orderIndex,
															addon.id!,
															addonGroup.id!,
															1,
															Number(addon.price || 0)
														);
													} else {
														handleAddonChange(
															orderIndex,
															addon.id!,
															addonGroup.id!,
															0,
															Number(addon.price || 0)
														);
													}
												}}
											/>
										}
										label={`${addon.name} (+${currency}${Number(addon.price || 0).toFixed(2)})`}
										style={{ margin: 0 }}
									/>
									{addon.is_multi && isSelected && (
										<InputField
											type="number"
											min="1"
											value={quantity}
											style={{ width: '60px', marginLeft: '10px', padding: '5px' }}
											onChange={(e) => {
												const newQuantity = Math.max(1, parseInt(e.target.value) || 1);
												handleAddonChange(
													orderIndex,
													addon.id!,
													addonGroup.id!,
													newQuantity,
													Number(addon.price || 0)
												);
											}}
										/>
									)}
								</div>
							);
						})}
					</div>
				))}
			</div>
		);
	};

	return (
		<>
			{orderVariants &&
				orderVariants.map((orderVariant: OrderVariantInterface, index: number) => {
					// Get the selected product to determine if it uses variants or addons
					const selectedProduct = orderVariant.product?.id ? getProductById(orderVariant.product.id) : null;
					const usesAddons = selectedProduct?.options_type === 'addon';

					return (
						<Box
							sx={variantItemWrapper}
							style={{
								width: '100%',
								backgroundColor: 'white',
								borderColor: '#c4c4c4',
							}}
							key={index}
						>
							<FormGroup sx={variantSelectStyle}>
								<Label>Product</Label>
								<Select
									name="product_id"
									value={orderVariant.product?.id || ''}
									onChange={(e) => handleChange(e, index, orderInventory)}
									style={{ padding: '15px' }}
								>
									<option value="">Select a product...</option>
									{orderInventory.map((product) => (
										<option value={product.id} key={product.id}>
											{product.name}
										</option>
									))}
								</Select>
							</FormGroup>

							{/* Show variant selection if product uses variants */}
							{selectedProduct && !usesAddons && (
								<FormGroup sx={variantSelectStyle}>
									<Label>Variant</Label>
									<Select
										name="id"
										value={orderVariant.id}
										onChange={(e) => handleChange(e, index, orderInventory)}
										style={{ padding: '15px' }}
									>
										<option value="">Select a variant...</option>
										{selectedProduct.variants?.map((variant: any) => (
											<option
												disabled={variant.total_quantity === 0}
												value={variant.variant_id}
												key={variant.variant_id}
											>
												{variant.variant_name}[{variant.total_quantity ?? 0}]
											</option>
										))}
									</Select>
								</FormGroup>
							)}

							{/* Show addon selection if product uses addons */}
							{selectedProduct &&
								usesAddons &&
								renderAddonSelection(orderVariant, index, selectedProduct)}

							<Box sx={variantInputWrapperStyle}>
								<FormGroup sx={variantUnitPriceWrapper}>
									<Label>Unit Price</Label>
									<InputField
										style={{
											padding: '15px',
											backgroundColor: usesAddons ? '#f9fafb' : 'white',
											color: usesAddons ? '#6b7280' : 'inherit',
										}}
										type="number"
										name="unit_price_at_purchase"
										value={Number(orderVariant.unit_price_at_purchase).toFixed(2)}
										onChange={(e) => handleChange(e, index)}
										disabled={usesAddons}
										step="0.01"
									/>
								</FormGroup>
								<FormGroup sx={variantUnitPriceWrapper}>
									<Label>Discount Amount</Label>
									<InputField
										style={{
											padding: '15px',
											backgroundColor: 'white',
											color: 'inherit',
										}}
										type="number"
										name="discount_amount"
										value={Number(orderVariant.discount_amount).toFixed(2)}
										onChange={(e) => handleChange(e, index)}
										step="0.01"
									/>
								</FormGroup>
								<FormGroup sx={variantQuantityWrapper}>
									<Label>Quantity</Label>
									<InputField
										min="1"
										type="number"
										name="quantity"
										value={orderVariant.quantity}
										style={{
											textAlign: 'center',
											padding: '15px',
											backgroundColor: 'white',
											color: 'inherit',
										}}
										onChange={(e) => handleChange(e, index)}
									/>
								</FormGroup>
								<div
									onClick={() => removeVariantSlot(index)}
									style={{
										marginLeft: '10px',
										marginTop: '20px',
										maxWidth: '15%',
										cursor: 'pointer',
									}}
								>
									<DeleteIcon />
								</div>
							</Box>
						</Box>
					);
				})}
			<AddVariantButton
				onClick={() => {
					if (orderInventory.length > 0) {
						const firstProduct = orderInventory[0];
						if (firstProduct.options_type === 'addon') {
							// For addon products, use the default variant
							if (firstProduct.default_variant) {
								addVariantSlot(
									Number(firstProduct.default_price),
									Number(firstProduct.default_variant.variant_id)
								);
							}
						} else {
							// For variant products, use the first variant
							if (firstProduct.variants && firstProduct.variants.length > 0) {
								addVariantSlot(
									Number(firstProduct.variants[0].price),
									Number(firstProduct.variants[0].variant_id)
								);
							}
						}
					}
				}}
			>
				Add New
			</AddVariantButton>
		</>
	);
};

const EditOrderForm = ({
	order: initialOrder,
	variants: initialVariants,
	isOrderVariantsLoading,
	cancelEdit,
	handleHidePanel,
	onDeleteClick,
}: EditOrderFormProps) => {
	const [order, setOrder] = useState<OrderInterface>(initialOrder);
	const [orderVariants, setOrderVariants] = useState<OrderVariantInterface[]>(initialVariants);
	const [isSubmittingOrder, setIsSubmittingOrder] = useState<boolean>(false);
	const [orderDate, setOrderDate] = useState<Moment | null>(null);
	const [customers, setCustomers] = useState<any[]>([]);
	const [isFetchingCustomers, setIsFetchingCustomers] = useState(false);

	useEffect(() => {
		// Load customer data if order has a customer_id
		if (order.customer_id && customers.length === 0) {
			fetchCustomers('')
				.then((response: any) => {
					setCustomers(Array.isArray(response) ? response : response.data || []);
				})
				.catch((error) => {
					console.error('Error fetching customers:', error);
					setCustomers([]);
				});
		}
	}, [order.customer_id]);

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		setOrder({ ...order, [name]: value });
		if (name === 'customer_name') {
			console.log('value', value);
			// setCustomers([{ customer_name: value }]);
		}
	};

	const handleCustomerSearch = async (searchTerm: string) => {
		if (searchTerm.length > 2) {
			setIsFetchingCustomers(true);
			try {
				const response = await fetchCustomers(searchTerm);
				setCustomers(Array.isArray(response) ? response : response.data || []);
			} catch (error) {
				console.error('Error fetching customers', error);
			} finally {
				setIsFetchingCustomers(false);
			}
		}
	};

	const handleCustomerChange = (customerData: any) => {
		setOrder((prevOrder) => ({
			...prevOrder,
			customer_id: customerData.id || null,
			customer_name: customerData.name || customerData.customer_name || '',
			customer_email: customerData.email || customerData.customer_email || '',
			customer_address: customerData.address || customerData.customer_address || '',
			customer_phone: customerData.phone || customerData.customer_phone || '',
			// Auto-populate order fields from customer data
			order_address: customerData.address || customerData.customer_address || prevOrder.order_address || '',
			order_contact: customerData.phone || customerData.customer_phone || prevOrder.order_contact || '',
		}));
	};

	const handleOrderVariantsChange = (
		e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
		index: number,
		orderInventory: any[] = []
	) => {
		const { name, value } = e.target;
		let newOrderVariants: OrderVariantInterface[] = [...orderVariants];

		if (name === 'product_id') {
			// When product changes, reset the variant data
			const selectedProduct = orderInventory.find((p) => p.id === Number(value));
			if (selectedProduct) {
				if (selectedProduct.options_type === 'addon') {
					// For addon products, use the default variant
					if (selectedProduct.default_variant) {
						newOrderVariants[index] = {
							...newOrderVariants[index],
							id: selectedProduct.default_variant.variant_id,
							unit_price_at_purchase: selectedProduct.default_price,
							product: { id: selectedProduct.id, name: selectedProduct.name },
							addons: [], // Reset addons when changing product
						};
					}
				} else {
					// For variant products, reset to select variant
					newOrderVariants[index] = {
						...newOrderVariants[index],
						id: 0, // Reset variant selection
						unit_price_at_purchase: selectedProduct.default_price,
						product: { id: selectedProduct.id, name: selectedProduct.name },
						addons: null,
					};
				}
			}
		} else if (name === 'id' && e.target instanceof HTMLSelectElement) {
			// When variant changes for variant-type products
			const selectedProduct = orderInventory.find((p) => p.id === newOrderVariants[index].product?.id);
			if (selectedProduct && selectedProduct.variants) {
				const selectedVariant = selectedProduct.variants.find((v: any) => v.variant_id === Number(value));
				if (selectedVariant) {
					newOrderVariants[index] = {
						...newOrderVariants[index],
						id: Number(value),
						unit_price_at_purchase: selectedVariant.price,
						product: selectedProduct,
					};
				}
			}
		} else {
			// Handle other field changes
			newOrderVariants[index] = {
				...newOrderVariants[index],
				[name]:
					name === 'unit_price_at_purchase' || name === 'discount_amount' || name === 'quantity'
						? Number(value)
						: value,
			};
		}

		setOrderVariants(newOrderVariants);
	};

	const handleAddonChange = (
		orderIndex: number,
		addonId: number,
		addonGroupId: number,
		quantity: number,
		price: number
	) => {
		let newOrderVariants = [...orderVariants];
		let currentAddons = newOrderVariants[orderIndex].addons || [];

		if (quantity === 0) {
			// Remove addon
			currentAddons = currentAddons.filter((addon: any) => addon.id !== addonId);
		} else {
			// Add or update addon
			const existingAddonIndex = currentAddons.findIndex((addon: any) => addon.id === addonId);
			if (existingAddonIndex >= 0) {
				currentAddons[existingAddonIndex] = {
					id: addonId,
					name: '', // This will be populated from the product data
					quantity,
					unit_price_at_purchase: price,
				};
			} else {
				currentAddons.push({
					id: addonId,
					name: '', // This will be populated from the product data
					quantity,
					unit_price_at_purchase: price,
				});
			}
		}

		newOrderVariants[orderIndex].addons = currentAddons;
		setOrderVariants(newOrderVariants);
	};

	// useEffect(() => {
	// 	console.log("order changed", order);
	// }, [order]);

	// useEffect(() => {
	// 	console.log("variants changed", orderVariants);
	// }, [orderVariants]);

	useEffect(() => {
		setOrder(initialOrder);
	}, [initialOrder]);

	useEffect(() => {
		setOrder({
			...order,
			backdated_order_date: orderDate,
		});
	}, [orderDate]);

	useEffect(() => {
		console.log('initialOrder', initialOrder);
		if (initialOrder) {
			if (initialOrder.backdated_order_date) {
				console.log('initialOrder.backdated_order_date', initialOrder.backdated_order_date);
				setOrderDate(moment(initialOrder.backdated_order_date));
			} else {
				console.log('initialOrder.date_created', initialOrder.date_created);
				setOrderDate(moment(initialOrder.date_created));
			}
		}
	}, [initialOrder]);

	useEffect(() => {
		setOrderVariants(initialVariants);
	}, [initialVariants]);

	const isInitialOrderEmpty = isEqual(initialOrder, {
		customer_name: '',
		customer_email: '',
		customer_phone: '',
		customer_address: '',
		order_address: '',
		order_contact: '',
		is_order_paid: false,
	});

	const handleSave = () => {
		setIsSubmittingOrder(true);

		let orderData = {
			...order,
			variants: orderVariants,
		};
		console.log('orderData', orderData);

		submitOrder(orderData)
			.then((response: any) => {
				handleHidePanel(response.data);
			})
			.catch((error: any) => {
				console.error('error submitting order', error);
			})
			.finally(() => {
				setIsSubmittingOrder(false);
			});
	};

	const addVariantSlot = (defaultPrice: number, defaultId: number) => {
		setOrderVariants([
			...orderVariants,
			{
				id: defaultId,
				unit_price_at_purchase: defaultPrice,
				quantity: 1,
				discount_amount: 0,
				total_price: defaultPrice,
				addons: null,
			},
		]);
	};

	const removeVariantSlot = (index: number) => {
		let newOrderVariants = [...orderVariants];
		newOrderVariants.splice(index, 1);
		setOrderVariants(newOrderVariants);
	};

	const handleDateChange = (date: Moment | null) => {
		setOrderDate(date);
	};

	return (
		<>
			<ModalTitle>{isInitialOrderEmpty ? 'Create' : 'Edit'} Order</ModalTitle>
			<Box sx={fieldRowStyle}>
				<FormGroup style={{ marginRight: '20px' }}>
					<Label style={{ visibility: 'hidden' }}>Ordered By</Label>
					{/* <InputField type="text" name="customer_name" value={order.customer_name} onChange={handleChange} /> */}
					<Autocomplete
						freeSolo
						options={customers}
						getOptionLabel={(option) => {
							if (typeof option === 'string') {
								return option;
							}
							return option.name || '';
						}}
						loading={isFetchingCustomers}
						onInputChange={(event, newInputValue) => {
							handleCustomerSearch(newInputValue);
							handleCustomerChange({ name: newInputValue });
						}}
						onChange={(event, newValue) => {
							if (newValue) {
								if (typeof newValue === 'string') {
									handleCustomerChange({ name: newValue });
								} else {
									handleCustomerChange({
										id: newValue.id,
										name: newValue.name,
										email: newValue.email,
										phone: newValue.phone,
										address: newValue.address,
									});
								}
							}
						}}
						value={
							order.customer_id
								? customers.find((c) => c.id === order.customer_id) || order.customer_name || ''
								: order.customer_name || ''
						}
						renderInput={(params) => (
							<TextField
								{...params}
								label="Ordered by"
								fullWidth
								InputProps={{
									...params.InputProps,
									endAdornment: (
										<>
											{isFetchingCustomers ? (
												<CircularProgress color="inherit" size={20} />
											) : null}
											{params.InputProps.endAdornment}
										</>
									),
								}}
							/>
						)}
					/>
					<div
						style={{
							color: '#3a5bd9',
							fontSize: '13px',
							fontStyle: 'italic',
							marginTop: '2px',
							marginLeft: '0px',
							fontWeight: '400',
							visibility:
								order.customer_name && order.customer_name.length > 0 && !order.customer_id
									? 'visible'
									: 'hidden',
						}}
					>
						**New Customer**
					</div>
				</FormGroup>
				<FormGroup>
					<Label>Delivery Address</Label>
					<InputField
						type="text"
						name="order_address"
						value={order.order_address || ''}
						onChange={handleChange}
					/>
				</FormGroup>
			</Box>
			<Box sx={fieldRowStyle}>
				<FormGroup style={{ marginRight: '20px' }}>
					<Label>Phone</Label>
					<InputField
						type="text"
						name="order_contact"
						value={order.order_contact || ''}
						onChange={handleChange}
					/>
				</FormGroup>
				<FormGroup>
					<Label>Email</Label>
					<InputField
						type="text"
						name="customer_email"
						value={order.customer_email}
						onChange={handleChange}
					/>
				</FormGroup>
			</Box>
			<FormGroup style={{ marginTop: '10px' }}>
				<CustomDatePicker label="Date Ordered" value={orderDate} onChange={handleDateChange} />
			</FormGroup>
			<hr style={{ margin: '50px 0px 30px 0px', backgroundColor: '#f3f3f3', border: 'none', height: '1px' }} />
			<div>
				<h4>Products</h4>
				{isOrderVariantsLoading ? (
					<div
						style={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', padding: '50px 0px' }}
					>
						<CircularProgress size={30} color="inherit" />
					</div>
				) : (
					<EditOrderModalProductsList
						isNewOrder={isInitialOrderEmpty}
						variants={orderVariants}
						handleChange={handleOrderVariantsChange}
						addVariantSlot={addVariantSlot}
						removeVariantSlot={removeVariantSlot}
						handleAddonChange={handleAddonChange}
					/>
				)}
			</div>
			<Box sx={fieldRowStyle}>
				<CreateButton onClick={handleSave} style={{ marginTop: '20px', width: '100%' }}>
					<CircularProgress
						style={{ visibility: isSubmittingOrder ? 'visible' : 'hidden', position: 'absolute' }}
						size={15}
						color="inherit"
					/>
					<span style={{ visibility: isSubmittingOrder ? 'hidden' : 'visible' }}>Save Order</span>
				</CreateButton>
			</Box>

			{/* Only show delete button for existing orders (orders with an ID) */}
			{initialOrder.id && onDeleteClick && (
				<Box sx={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #e5e7eb' }}>
					<CreateButton
						onClick={onDeleteClick}
						style={{ marginTop: '20px', width: '100%', borderColor: '#dc2626', color: '#dc2626' }}
						outline
					>
						<DeleteIcon style={{ fill: '#dc2626' }} />
						<span>Delete Order</span>
					</CreateButton>
				</Box>
			)}
		</>
	);
};

export default EditOrderForm;
